import type {
  NodeId,
  SurveyNode,
  SurveySchema,
  TextareaNode,
  TextNode,
} from '../dto/survey/survey-schema.types';
import { getSurveyAnagraficaSource, SURVEY_DEFAULT_SOURCE_NODE_TYPES } from './anagrafica-defaults';
import { reachableFrom } from './walk-path';

export type SchemaError =
  | { code: 'UNREACHABLE_NODE'; nodeId: NodeId }
  | { code: 'CYCLE_DETECTED'; cycle: NodeId[] }
  | { code: 'JUMP_TO_MISSING_NODE'; fromNodeId: NodeId; targetId: string }
  | { code: 'JUMP_OPTION_MISMATCH'; nodeId: NodeId; optionId: string }
  | { code: 'NO_PATH_TO_END' }
  | { code: 'MISSING_I18N'; nodeId: NodeId; field: 'it' | 'en' }
  | { code: 'REPEAT_SOURCE_INVALID'; nodeId: NodeId }
  | { code: 'REPEAT_SOURCE_NOT_PRECEDING'; nodeId: NodeId }
  | { code: 'REPEAT_NESTED'; nodeId: NodeId }
  | { code: 'MISSING_START_NODE' }
  | { code: 'DUPLICATE_NODE_ID'; nodeId: NodeId }
  | { code: 'AI_PROMPT_REQUIRED'; nodeId: NodeId; field: 'it' | 'en' }
  | { code: 'END_MESSAGE_I18N'; nodeId: NodeId; field: 'it' | 'en' }
  | { code: 'END_FINISH_INVALID'; nodeId: NodeId; finish: string }
  | { code: 'END_IN_REPEAT'; nodeId: NodeId }
  | { code: 'DEFAULT_SOURCE_UNKNOWN'; nodeId: NodeId; sourceId: string }
  | { code: 'DEFAULT_SOURCE_NODE_TYPE'; nodeId: NodeId; nodeType: string; sourceId: string }
  | { code: 'DEFAULT_SOURCE_IN_REPEAT'; nodeId: NodeId }
  | { code: 'ENTER_WHEN_INVALID'; nodeId: NodeId };

const END = 'END';

function flatten(nodes: SurveyNode[]): SurveyNode[] {
  const out: SurveyNode[] = [];
  for (const n of nodes) {
    out.push(n);
    if (n.type === 'repeat-block') {
      out.push(...flatten(n.innerNodes));
    }
  }
  return out;
}

function nextTargets(node: SurveyNode): string[] {
  if (node.type === 'end') {
    return [];
  }
  const n = (node as Exclude<SurveyNode, { type: 'end' }>).next;
  if (!n) {
    return [];
  }
  if (n.kind === 'linear') {
    return [n.nextNodeId];
  }
  if (n.kind === 'jump-by-option') {
    return [n.default, ...Object.values(n.perOption)];
  }
  return [n.whenTrue, n.whenFalse];
}

function checkI18n(all: SurveyNode[]): SchemaError[] {
  const errors: SchemaError[] = [];
  for (const n of all) {
    if (!n.labels?.it?.trim()) {
      errors.push({ code: 'MISSING_I18N', nodeId: n.id, field: 'it' });
    }
    if (!n.labels?.en?.trim()) {
      errors.push({ code: 'MISSING_I18N', nodeId: n.id, field: 'en' });
    }
  }
  return errors;
}

function checkMissingTargets(all: SurveyNode[], byId: Map<NodeId, SurveyNode>): SchemaError[] {
  const errors: SchemaError[] = [];
  for (const n of all) {
    for (const t of nextTargets(n)) {
      if (t !== END && !byId.has(t)) {
        errors.push({ code: 'JUMP_TO_MISSING_NODE', fromNodeId: n.id, targetId: t });
      }
    }
  }
  return errors;
}

function checkOptionMismatches(all: SurveyNode[]): SchemaError[] {
  const errors: SchemaError[] = [];
  for (const n of all) {
    if (n.type !== 'single-choice' && n.type !== 'multi-choice') {
      continue;
    }
    if (n.next?.kind !== 'jump-by-option') {
      continue;
    }
    const optionIds = new Set(n.options.map((o) => o.id));
    for (const key of Object.keys(n.next.perOption)) {
      if (!optionIds.has(key)) {
        errors.push({ code: 'JUMP_OPTION_MISMATCH', nodeId: n.id, optionId: key });
      }
    }
  }
  return errors;
}

/**
 * Structural checks for `enterWhen` (`validateOrThrow` runs at submit time
 * too, so these must reject anything `resolve-next.ts`'s
 * `skipUnmetEnterWhenNodes` cannot safely walk): the guard is never on the
 * schema's own entry point nor inside a repeat-block (both have no single
 * well-defined "skip past me" edge), the guarded node's own `next` must be
 * `linear` (the skip target `resolveNext` falls back to), every referenced
 * source node must exist OUTSIDE a repeat-block and be a genuine ancestor of
 * the guarded node ON THE GRAPH — reachable from the source, and never
 * reachable back from the guarded node to the source (not
 * `schema.nodes` array order: a node's position in that array says nothing
 * about which edges actually lead to it, so a source listed earlier than its
 * guarded node can still be structurally downstream of it, e.g. behind a
 * jump) — and every referenced option id must exist on that source node's
 * own catalog. `anyOf` must be non-empty and every ref's `optionIds`
 * non-empty: an empty `anyOf` can never be satisfied (`isEnterWhenSatisfied`
 * uses `.some`, false on `[]`) and an empty `optionIds` is a ref that can
 * never match (`.includes`/`.some` on `[]`), so both would ship a node that
 * is either always skipped or contributes a permanently-dead OR branch.
 */
function checkEnterWhen(
  all: SurveyNode[],
  schema: SurveySchema,
  byId: Map<NodeId, SurveyNode>,
  repeatInnerIds: Set<NodeId>,
): SchemaError[] {
  const errors: SchemaError[] = [];
  for (const n of all) {
    if (!n.enterWhen) {
      continue;
    }
    if (n.id === schema.startNodeId || repeatInnerIds.has(n.id)) {
      errors.push({ code: 'ENTER_WHEN_INVALID', nodeId: n.id });
      continue;
    }
    const hasLinearNext = n.type !== 'end' && n.next?.kind === 'linear';
    if (
      !hasLinearNext ||
      !isEnterWhenStructurallyValid(n.enterWhen, n.id, schema, byId, repeatInnerIds)
    ) {
      errors.push({ code: 'ENTER_WHEN_INVALID', nodeId: n.id });
    }
  }
  return errors;
}

function isEnterWhenStructurallyValid(
  enterWhen: NonNullable<SurveyNode['enterWhen']>,
  guardedNodeId: NodeId,
  schema: SurveySchema,
  byId: Map<NodeId, SurveyNode>,
  repeatInnerIds: Set<NodeId>,
): boolean {
  if (enterWhen.anyOf.length === 0) {
    return false;
  }
  const reachableFromGuarded = reachableFrom(schema, [guardedNodeId]);
  return enterWhen.anyOf.every((ref) => {
    if (ref.optionIds.length === 0 || repeatInnerIds.has(ref.nodeId)) {
      return false;
    }
    // The source must be a genuine ancestor: some path reaches the guarded
    // node FROM it, and the guarded node never reaches back to it (which
    // would make it a descendant, or put both on a cycle — either way the
    // source could not possibly have been answered yet when the guarded
    // node's own entry is evaluated).
    if (
      reachableFromGuarded.has(ref.nodeId) ||
      !reachableFrom(schema, [ref.nodeId]).has(guardedNodeId)
    ) {
      return false;
    }
    const source = byId.get(ref.nodeId);
    if (!source || (source.type !== 'single-choice' && source.type !== 'multi-choice')) {
      return false;
    }
    const validOptionIds = new Set(source.options.map((o) => o.id));
    return ref.optionIds.every((optionId) => validOptionIds.has(optionId));
  });
}

type ReachabilityResult = {
  errors: SchemaError[];
  reachable: Set<NodeId>;
};

function checkReachabilityAndCycles(
  schema: SurveySchema,
  byId: Map<NodeId, SurveyNode>,
): ReachabilityResult {
  const errors: SchemaError[] = [];
  const color = new Map<NodeId, 0 | 1 | 2>();
  const stack: NodeId[] = [];
  let reachedEnd = false;

  const dfs = (id: NodeId): void => {
    if (id === END) {
      reachedEnd = true;
      return;
    }
    const c = color.get(id) ?? 0;
    if (c === 1) {
      errors.push({ code: 'CYCLE_DETECTED', cycle: [...stack, id] });
      return;
    }
    if (c === 2) {
      return;
    }
    color.set(id, 1);
    stack.push(id);
    const node = byId.get(id);
    if (node) {
      // An explicit `type: 'end'` node is a terminal just like the literal
      // 'END' target: reaching it satisfies NO_PATH_TO_END (it has no next
      // targets, so the walk would otherwise finish without "reaching END").
      if (node.type === 'end') {
        reachedEnd = true;
      }
      for (const t of nextTargets(node)) {
        dfs(t);
      }
    }
    stack.pop();
    color.set(id, 2);
  };

  dfs(schema.startNodeId);
  if (!reachedEnd) {
    errors.push({ code: 'NO_PATH_TO_END' });
  }

  const reachable = new Set([...color.entries()].filter(([, v]) => v === 2).map(([k]) => k));
  return { errors, reachable };
}

function checkRepeatFromAnswer(
  n: Extract<SurveyNode, { type: 'repeat-block' }>,
  byId: Map<NodeId, SurveyNode>,
  topLevelNodes: SurveyNode[],
): SchemaError[] {
  if (n.iterationSource.kind !== 'from-answer') {
    return [];
  }
  const src = byId.get(n.iterationSource.answerNodeId);
  if (!src || src.type !== 'number') {
    return [{ code: 'REPEAT_SOURCE_INVALID', nodeId: n.id }];
  }
  const idxSrc = topLevelNodes.findIndex((x) => x.id === src.id);
  const idxRb = topLevelNodes.findIndex((x) => x.id === n.id);
  if (idxSrc < 0 || idxRb < 0 || idxSrc >= idxRb) {
    return [{ code: 'REPEAT_SOURCE_NOT_PRECEDING', nodeId: n.id }];
  }
  return [];
}

function checkRepeatBlocks(
  all: SurveyNode[],
  byId: Map<NodeId, SurveyNode>,
  topLevelNodes: SurveyNode[],
): SchemaError[] {
  const errors: SchemaError[] = [];
  for (const n of all) {
    if (n.type !== 'repeat-block') {
      continue;
    }
    if (n.innerNodes.some((c) => c.type === 'repeat-block')) {
      errors.push({ code: 'REPEAT_NESTED', nodeId: n.id });
    }
    errors.push(...checkRepeatFromAnswer(n, byId, topLevelNodes));
  }
  return errors;
}

export function validateSchema(schema: SurveySchema): SchemaError[] {
  const all = flatten(schema.nodes);
  const byId = new Map<NodeId, SurveyNode>();
  const errors: SchemaError[] = [];
  for (const n of all) {
    if (byId.has(n.id)) {
      errors.push({ code: 'DUPLICATE_NODE_ID', nodeId: n.id });
      continue;
    }
    byId.set(n.id, n);
  }

  if (!byId.has(schema.startNodeId)) {
    errors.push({ code: 'MISSING_START_NODE' });
  }

  errors.push(...checkI18n(all));
  errors.push(...checkMissingTargets(all, byId));
  errors.push(...checkOptionMismatches(all));

  // Collect IDs that live inside a repeat-block — they are always reachable via
  // the block iteration and must be excluded from the top-level reachability check.
  const innerNodeIds = new Set<NodeId>();
  for (const n of schema.nodes) {
    if (n.type === 'repeat-block') {
      for (const inner of flatten(n.innerNodes)) {
        innerNodeIds.add(inner.id);
      }
    }
  }

  const { errors: reachErrors, reachable } = checkReachabilityAndCycles(schema, byId);
  errors.push(...reachErrors);

  for (const n of all) {
    if (!reachable.has(n.id) && !innerNodeIds.has(n.id)) {
      errors.push({ code: 'UNREACHABLE_NODE', nodeId: n.id });
    }
  }

  errors.push(...checkRepeatBlocks(all, byId, schema.nodes));
  errors.push(...checkNodeConfig(all));
  errors.push(...checkEndNodes(all, innerNodeIds));
  errors.push(...checkDefaultSources(all, innerNodeIds));
  errors.push(...checkEnterWhen(all, schema, byId, innerNodeIds));

  return errors;
}

function checkNodeConfig(all: SurveyNode[]): SchemaError[] {
  const errors: SchemaError[] = [];
  for (const n of all) {
    const aiNode =
      n.type === 'textarea' ? (n as TextareaNode) : n.type === 'text' ? (n as TextNode) : null;
    if (aiNode?.aiAssist) {
      const prompt = aiNode.aiPrompt;
      if (!prompt?.it?.trim()) {
        errors.push({ code: 'AI_PROMPT_REQUIRED', nodeId: n.id, field: 'it' });
      }
      if (!prompt?.en?.trim()) {
        errors.push({ code: 'AI_PROMPT_REQUIRED', nodeId: n.id, field: 'en' });
      }
    }
  }
  return errors;
}

// `finish` is optional (absent = 'submit'); anything other than the two
// known values is rejected so a typo can never silently disable the submit.
const END_FINISH_VALUES = new Set(['submit', 'exit']);

function checkEndNodes(all: SurveyNode[], repeatInnerIds: Set<NodeId>): SchemaError[] {
  const errors: SchemaError[] = [];
  for (const n of all) {
    if (n.type !== 'end') {
      continue;
    }
    // Fail-closed on the closure copy: the exit/submit screen renders
    // node.message in both locales, so an empty translation would show a
    // blank panel to the consultant.
    if (!n.message?.it?.trim()) {
      errors.push({ code: 'END_MESSAGE_I18N', nodeId: n.id, field: 'it' });
    }
    if (!n.message?.en?.trim()) {
      errors.push({ code: 'END_MESSAGE_I18N', nodeId: n.id, field: 'en' });
    }
    if (n.finish !== undefined && !END_FINISH_VALUES.has(n.finish)) {
      errors.push({ code: 'END_FINISH_INVALID', nodeId: n.id, finish: String(n.finish) });
    }
    // An end node inside a repeat-block would terminate the whole survey in
    // the middle of an iteration (resolve-next stops on any `end`); the
    // submit walker treats it as terminal too, so it is structurally invalid.
    if (repeatInnerIds.has(n.id)) {
      errors.push({ code: 'END_IN_REPEAT', nodeId: n.id });
    }
  }
  return errors;
}

function checkDefaultSources(all: SurveyNode[], repeatInnerIds: Set<NodeId>): SchemaError[] {
  const errors: SchemaError[] = [];
  for (const n of all) {
    const sourceId = n.defaultValueSource;
    if (!sourceId) {
      continue;
    }
    // Repeat-block inner nodes are excluded from prefill in v1: enforce strictly.
    if (repeatInnerIds.has(n.id)) {
      errors.push({ code: 'DEFAULT_SOURCE_IN_REPEAT', nodeId: n.id });
      continue;
    }
    if (!getSurveyAnagraficaSource(sourceId)) {
      errors.push({ code: 'DEFAULT_SOURCE_UNKNOWN', nodeId: n.id, sourceId });
      continue;
    }
    if (!SURVEY_DEFAULT_SOURCE_NODE_TYPES.some((t) => t === n.type)) {
      errors.push({ code: 'DEFAULT_SOURCE_NODE_TYPE', nodeId: n.id, nodeType: n.type, sourceId });
    }
  }
  return errors;
}
